import { handleTextInputNumberChange } from 'helpers';
import React, { useState } from 'react';
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { InitContextProps, useAuthContext } from 'services/context/authentication.context';
import CustomerCardService from 'services/customer-card.service';
import AddressAutocomplete, { COUNTY_LIST } from 'components/AddressAutocomplete/AddressAutocomplete';
import { isEmailValidAndPresent } from 'helpers/validators';

interface IdCreditRequestModalProps {
    handleConfirmSubmit: () => void
    handleConfirmReject: () => void
}

interface NewIdCreditCardForm {
    email: string;
    phone: string;
    pinCode: string;
    address: string;
    city: string;
    county: string;
    zip: string;
    termsAgreed: boolean;
    includeRfidCard: boolean;
}

export function IdCreditRequestModal(props: IdCreditRequestModalProps) {
    const { state }: InitContextProps = useAuthContext();
    const { customerId, customer } = state;

    const [saving, setSaving]: [boolean, any] = useState(false);
    const [savingError, setSavingError]: [boolean, any] = useState(false);
    const [validated, setValidated]: [boolean, any] = useState(false);
    const [isSubmitted, setSubmitted] = useState(false);
    const [form, setForm]: [NewIdCreditCardForm, any] = useState({
        email: customer.email,
        phone: customer.phone.replace(/^(\+)?372/g, ''),
        address: '',
        city: '',
        county: '',
        zip: '',
        pinCode: '',
        termsAgreed: false,
        includeRfidCard: false
    });

    const rejectSubmit = () => {
        props.handleConfirmReject();
    };
    const onPinChange = (newValue: string) => {
        if (newValue.length > 4 || (newValue.length && isNaN(Number(newValue)))) {
            return;
        }
        setForm({ ...form, pinCode: newValue.trim() });
    }

    const confirmSubmit = () => {
        setValidated(false);
        const isValidForm = validateForm();
        setValidated(true);
        if (isValidForm) {
            handleSubmit();
        } else {
            setSaving(false);
        }
    };

    const validateForm = () => {
        if (isInvalidEmail)
            return false;
        if (!form.phone.trim().length)
            return false;
        if (form.pinCode.length !== 4 || isNaN(Number(form.pinCode)))
            return false;
        if (!form.termsAgreed)
            return false;
        if (form.zip.length !== 5 || isNaN(Number(form.zip))) {
            return false;
        }
        if (!form.city || !form.address || !form.county) {
            return false;
        }
        return true;
    };

    const handleSubmit = () => {
        setSaving(true);
        setSavingError(false);

        if (!customerId || !customer || (customer && !customer.contactId))
            return;

        CustomerCardService.postIdCreditCardRequest({
            email: form.email,
            phone: form.phone,
            pinCode: form.pinCode,
            address: form.address,
            city: form.city,
            county: form.county,
            zip: form.zip,
            includeRfidCard: form.includeRfidCard
        }).then(() => {
            setSaving(false);
            setSubmitted(true);
        }).catch((reason: any) => {
            console.error('Saving failed', reason);
            setSaving(false);
            setSavingError(true);
        });
    };

    const confirmFinalMessage = () => {
        props.handleConfirmSubmit();
    }

    const isInvalidEmail = !isEmailValidAndPresent(form.email);

    return (
        <>
            <Modal
                show={true}
                size={"lg"}
                aria-labelledby="contained-modal-title-vcenter"
                centered
                onHide={rejectSubmit}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        ID-krediitkaardi taotlemine
                    </Modal.Title>
                </Modal.Header>

                {!isSubmitted && <><Modal.Body>
                    <Form noValidate validated={validated} onSubmit={(e: any) => e.preventDefault()}
                        id={"card-form"}
                        className={'mt-4 align-self-center m-0'}
                    >
                        <div className="row mt-2">
                            <Form.Group controlId={'form-email'} className={'col-12 col-md-8 col-lg-6'}>
                                <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                                    <span className="text-nowrap">E-posti aadress</span>
                                </Form.Label>
                                <Form.Control 
                                    required 
                                    className={''}
                                    minLength={1}
                                    value={form.email}
                                    onChange={(e) => setForm({
                                        ...form,
                                        email: e.target.value
                                    })
                                    }
                                    disabled={saving}
                                    type="text" 
                                    isInvalid={isInvalidEmail}
                                />
                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group controlId={'form-phone'} className={'col-12 col-md-8 col-lg-6'}>
                                <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                                    <span className="text-nowrap">Telefoninumber</span>
                                </Form.Label>
                                <div className="d-flex flex-row justify-content-center">
                                    <input
                                    type="text"
                                    className="mr-1 form-control"
                                    style={{ maxWidth: '70px' }}
                                    placeholder='+372'
                                    disabled
                                    />
                                    <input
                                    type="tel"
                                    className="mobile-id-input__main form-control"
                                    placeholder='Telefoninumber'
                                    disabled={saving}
                                    value={form.phone}
                                    onChange={(e) => setForm({...form, phone: handleTextInputNumberChange(form.phone, e.target.value)})}
                                    />
                                </div>
                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </Form.Group>
                        </div>

                        <div className="row mt-2">
                            <Form.Group controlId={'form-address'}
                                className={'col-12 mb-1'}>

                            </Form.Group>
                            <div className="form-group col-12 mb-1">
                                <Form.Label>
                                    <span className="text-nowrap">Aadress (tänav, maja, korter)</span>
                                </Form.Label>
                                <AddressAutocomplete
                                    address={form.address}
                                    setAddress={(address) => setForm({
                                        ...form,
                                        address: address.address || form.address,
                                        city: address.city || form.city,
                                        county: address.county || form.county,
                                        zip: address.zip || form.zip
                                    })}
                                    disabled={saving}
                                />
                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </div>
                        </div>

                        <div className="form-row">
                            <Form.Group controlId={'form-city'}
                                className={'col-12 col-md-6 col-lg-4 mb-1'}>
                                <Form.Label>
                                    <span className="text-nowrap">Linn/vald</span>
                                </Form.Label>
                                <Form.Control required
                                    value={form.city}
                                    disabled={saving}
                                    onChange={(e) => setForm({
                                        ...form,
                                        city: e.target.value
                                    })}
                                    type="text"
                                    autoComplete="address-level2"
                                />
                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group controlId={'form-county'}
                                className={'col-12 col-md-6 col-lg-4 mb-1'}>
                                <Form.Label>
                                    <span className="text-nowrap">Maakond</span>
                                </Form.Label>
                                <select className="form-control" required value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })}>
                                    <option value="">Vali maakond...</option>
                                    { COUNTY_LIST.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>                               
                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group controlId={'form-zip'}
                                className={'col-12 col-md-6 col-lg-4 mb-1'}>
                                <Form.Label>
                                    <span className="text-nowrap">Postikood</span>
                                </Form.Label>
                                <Form.Control required
                                    minLength={5}
                                    maxLength={5}
                                    value={form.zip}
                                    disabled={saving}
                                    pattern="^[0-9]{5}"
                                    onChange={(e) => setForm({
                                        ...form,
                                        zip: e.target.value
                                    })}
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="postal-code"
                                />
                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </Form.Group>                            
                        </div>                        

                        <div className="form-row mt-2">
                            <Form.Group controlId={'form-card-month-limit'}
                                className={'col-12 col-md-6 col-lg-4'}>
                                <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                                    <span className="text-nowrap">PIN kood, mida soovite tankimisel kasutada</span>
                                </Form.Label>
                                <Form.Control required isValid={form.pinCode.length === 4}
                                    minLength={4}
                                    maxLength={4}
                                    value={form.pinCode}
                                    disabled={saving}
                                    pattern="^[0-9]{4}"
                                    onChange={(e) => onPinChange(e.target.value)}
                                    type="password"
                                    autoComplete="off"
                                />
                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </Form.Group>
                        </div>
                        <div className="row">
                            <div className="col-12 font-weight-bold">
                                Krediidiotsuse tegemiseks teeme Teie kohta Teie andmeid kasutades järelpärimise maksehäireregistritesse (nt. Taust.ee, creditinfo.ee)
                            </div>
                        </div>
                        <div className="row mt-2">
                            <Form.Group controlId="termsAgreed" className={'col-12 col-lg-4'}>
                                <Form.Check
                                    custom
                                    required
                                    type="checkbox"
                                    label="Nõustun järelpärimisega"
                                    value={form.termsAgreed ? 'true' : 'false'}
                                    onChange={(e: any) => setForm({
                                        ...form,
                                        termsAgreed: !form.termsAgreed
                                    })}
                                />
                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </Form.Group>
                        </div>
                        <div className="row mt-2">
                            <Form.Group controlId="includeRFID" className={'col-12'}>
                                <Form.Check
                                    custom
                                    type="checkbox"
                                    label="Soovin tellida RFID kaarti elektriauto laadimiseks (laadimine võimalik ka ilma selleta läbi Cloudics rakenduse)"
                                    value={form.includeRfidCard ? 'true' : 'false'}
                                    onChange={(e: any) => setForm({
                                        ...form,
                                        includeRfidCard: !form.includeRfidCard
                                    })}
                                />
                            </Form.Group>
                        </div>                        
                    </Form>
                </Modal.Body>
                <Modal.Footer className="d-flex flex-row align-items-end justify-content-around flex-wrap mb-1">
                    <Button variant="outline-dark"
                        onClick={() => rejectSubmit()}
                        type="button"
                        disabled={saving}
                        className={"d-flex align-items-center"}>
                        <span className='px-2'>Loobu</span>
                    </Button>
                    <Button variant="primary" onClick={confirmSubmit} type="button" disabled={saving}
                        className="d-flex align-items-center">
                        {
                            !saving &&
                            <span className='px-2'>Saada taotlus</span>
                        }
                        {
                            saving &&
                            <>
                                <span className='px-2'>Salvestab…</span>
                                <Spinner size={"sm"} animation={"border"} />
                            </>
                        }
                    </Button>
                </Modal.Footer>
                </>
                }
                {
                    isSubmitted &&
                    <>
                        <Modal.Body>
                            <p>
                                <strong>Taotlus esitatud!</strong><br />
                                Anname Teile oma otsusest teada e-maili teel esimesel võimalusel
                            </p>
                        </Modal.Body>
                        <Modal.Footer className="d-flex flex-row align-items-end justify-content-around flex-wrap mb-1">
                            <Button variant="primary" onClick={confirmFinalMessage} type="button"
                                    className="d-flex align-items-center">
                                    <span className='px-2'>Sulge</span>
                            </Button>
                        </Modal.Footer>
                    </>
                }                
                {
                    !saving && savingError &&
                    <span className="d-flex justify-content-end text-danger px-2 pb-2">Viga salvestamisel, proovi mõne hetke pärast uuesti.</span>
                }
            </Modal>
        </>
    );
}
